import { db, auth, firebaseConfig } from '../lib/firebase';
import {
  ref,
  query,
  orderByKey,
  limitToLast,
  get,
  update,
  push,
  serverTimestamp,
  child,
  orderByChild,
  runTransaction
} from 'firebase/database';
import { CrimeCase, GameLeaderboardEntry, GameAttempt, Suspect, Clue, Question, Tip, Timeline, Evidence, Solution, ScoringConfig } from '../types/crime-game';
import { incrementChallengeScore } from './challengeService';
import { updateUserScores } from './leaderboardService';
import { logUserActivity } from "./activityLogService";

// Fetch the latest available date key (e.g., "30-12-2025")
export async function fetchLatestCrimeDate(): Promise<string | null> {
  try {
    // Attempt to fetch all keys via REST API with shallow=true to avoid downloading all data
    // This allows us to sort dates chronologically instead of lexicographically
    let url = `${firebaseConfig.databaseURL}/crimes.json?shallow=true`;

    // Add auth token if user is logged in, just in case rules require it
    const user = auth.currentUser;
    if (user) {
      try {
        const token = await user.getIdToken();
        url += `&auth=${token}`;
      } catch (e) {
        // If token fetch fails, try without auth
        console.warn("Failed to get auth token for latest crime date fetch, proceeding without auth.");
      }
    }

    const response = await fetch(url);
    if (!response.ok) {
      // If REST API fails (e.g. 401), throw to fallback
      throw new Error(`REST API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    if (!data) return null;

    const keys = Object.keys(data);
    if (keys.length === 0) return null;

    // Parse keys and find the latest
    // Format is DD-MM-YYYY
    keys.sort((a, b) => {
      const partsA = a.split('-');
      const partsB = b.split('-');

      // Safety check for format
      if (partsA.length !== 3 || partsB.length !== 3) return 0;

      const [d1, m1, y1] = partsA.map(Number);
      const [d2, m2, y2] = partsB.map(Number);

      const date1 = new Date(y1, m1 - 1, d1);
      const date2 = new Date(y2, m2 - 1, d2);

      return date2.getTime() - date1.getTime(); // Descending
    });

    return keys[0];

  } catch (error) {
    console.error("Error fetching latest crime date via REST, falling back to SDK:", error);
    // Fallback to SDK (Note: This has the lexicographical sort issue for DD-MM-YYYY keys)
    const crimesRef = ref(db, 'crimes');
    const latestQuery = query(crimesRef, orderByKey(), limitToLast(1));
    const snapshot = await get(latestQuery);

    if (snapshot.exists()) {
      const keys = Object.keys(snapshot.val());
      return keys.length > 0 ? keys[0] : null;
    }
    return null;
  }
}

// Fetch all available date keys, excluding the current date
export async function fetchAllCrimeDates(excludeDate?: string): Promise<string[]> {
  try {
    // Attempt to fetch all keys via REST API with shallow=true
    let url = `${firebaseConfig.databaseURL}/crimes.json?shallow=true`;

    // Add auth token if user is logged in
    const user = auth.currentUser;
    if (user) {
      try {
        const token = await user.getIdToken();
        url += `&auth=${token}`;
      } catch (e) {
        console.warn("Failed to get auth token for crime dates fetch, proceeding without auth.");
      }
    }

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`REST API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    if (!data) return [];

    let keys = Object.keys(data);
    
    // Filter out the current date if provided
    if (excludeDate) {
      keys = keys.filter(key => key !== excludeDate);
    }

    if (keys.length === 0) return [];

    // Sort dates chronologically (descending - newest first)
    keys.sort((a, b) => {
      const partsA = a.split('-');
      const partsB = b.split('-');

      // Safety check for format
      if (partsA.length !== 3 || partsB.length !== 3) return 0;

      const [d1, m1, y1] = partsA.map(Number);
      const [d2, m2, y2] = partsB.map(Number);

      const date1 = new Date(y1, m1 - 1, d1);
      const date2 = new Date(y2, m2 - 1, d2);

      return date2.getTime() - date1.getTime(); // Descending
    });

    return keys;

  } catch (error) {
    console.error("Error fetching crime dates via REST, falling back to SDK:", error);
    // Fallback to SDK
    try {
      const crimesRef = ref(db, 'crimes');
      const snapshot = await get(crimesRef);

      if (snapshot.exists()) {
        let keys = Object.keys(snapshot.val());
        
        // Filter out the current date if provided
        if (excludeDate) {
          keys = keys.filter(key => key !== excludeDate);
        }

        // Sort dates chronologically
        keys.sort((a, b) => {
          const partsA = a.split('-');
          const partsB = b.split('-');

          if (partsA.length !== 3 || partsB.length !== 3) return 0;

          const [d1, m1, y1] = partsA.map(Number);
          const [d2, m2, y2] = partsB.map(Number);

          const date1 = new Date(y1, m1 - 1, d1);
          const date2 = new Date(y2, m2 - 1, d2);

          return date2.getTime() - date1.getTime();
        });

        return keys;
      }
    } catch (fallbackError) {
      console.error("Error in fallback fetch:", fallbackError);
    }
    return [];
  }
}

// Helper functions for date handling
function parseDateStr(dateStr: string): Date {
  const [d, m, y] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function getTodayString(): string {
  const now = new Date();
  const d = String(now.getDate()).padStart(2, '0');
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const y = now.getFullYear();
  return `${d}-${m}-${y}`;
}

function isConsecutiveDay(prevDateStr: string, currDateStr: string): boolean {
  const prev = parseDateStr(prevDateStr);
  const curr = parseDateStr(currDateStr);

  const prevUtc = Date.UTC(prev.getFullYear(), prev.getMonth(), prev.getDate());
  const currUtc = Date.UTC(curr.getFullYear(), curr.getMonth(), curr.getDate());

  const msPerDay = 1000 * 60 * 60 * 24;
  return (currUtc - prevUtc) === msPerDay;
}

function isSameDay(dateStr1: string, dateStr2: string): boolean {
  return dateStr1 === dateStr2;
}

function isAfterConsecutive(prevDateStr: string, currDateStr: string): boolean {
  const prev = parseDateStr(prevDateStr);
  const curr = parseDateStr(currDateStr);

  const prevUtc = Date.UTC(prev.getFullYear(), prev.getMonth(), prev.getDate());
  const currUtc = Date.UTC(curr.getFullYear(), curr.getMonth(), curr.getDate());

  const msPerDay = 1000 * 60 * 60 * 24;
  return (currUtc - prevUtc) > msPerDay;
}

// Helper to normalize the compact JSON data structure
// Exporting for testing purposes
export function normalizeRawCase(data: any, index: number = 0): CrimeCase {
  // Helper to find a value case-insensitively
  const getValue = (keys: string[]) => {
    for (const k of keys) {
      if (data[k] !== undefined) return data[k];
    }
    return undefined;
  };

  // 1. Parse Suspects (data.ch - tuple format: [id, name, bio, isSuspect])
  let suspects: Suspect[] = [];
  const rawSuspects = getValue(['ch', 'Ch', 'CH', 'suspects', 'Suspects']) || [];

  if (Array.isArray(rawSuspects)) {
    suspects = rawSuspects.map((item: any) => {
      if (Array.isArray(item)) {
        return {
          id: item[0] || '',
          name: item[1] || '',
          bio: item[2] || '',
          isSuspect: item[3] === 1 || item[3] === true
        };
      }
      // Already an object
      return {
        id: item.id || '',
        name: item.name || '',
        bio: item.bio || '',
        isSuspect: item.isSuspect !== undefined ? item.isSuspect : true
      };
    });
  }

  // 2. Parse Questions (data.q - tuple format: [id, question, options, correctIndex])
  let questions: Question[] = [];
  const rawQuestions = getValue(['q', 'Q', 'questions', 'Questions']) || [];

  if (Array.isArray(rawQuestions)) {
    questions = rawQuestions.map((item: any) => {
      if (Array.isArray(item)) {
        // Handle two possible formats:
        // 1. Correct format: [id, question, options[], correctIndex]
        // 2. Incorrect format: [id, question, correctAnswer, acceptableAnswers[]]
        let options: string[] = [];
        let correctIndex = 0;

        if (Array.isArray(item[2])) {
          // Correct format: item[2] is options array, item[3] is correctIndex
          options = item[2];
          correctIndex = typeof item[3] === 'number' ? item[3] : 0;
        } else if (Array.isArray(item[3])) {
          // Incorrect format: item[2] is correctAnswer string, item[3] is acceptableAnswers array
          // Convert acceptableAnswers to options and find the correct index
          options = item[3];
          const correctAnswer = typeof item[2] === 'string' ? item[2] : '';
          // Find the index of the correct answer in the options array (case-insensitive)
          correctIndex = options.findIndex(
            (opt: string) => opt.toLowerCase().trim() === correctAnswer.toLowerCase().trim()
          );
          // If not found, default to 0
          if (correctIndex === -1) {
            correctIndex = 0;
          }
        } else {
          // Fallback: try to use item[2] as options if it's an array, otherwise empty
          options = Array.isArray(item[2]) ? item[2] : [];
          correctIndex = typeof item[3] === 'number' ? item[3] : 0;
        }

        // Validate correctIndex is within bounds
        const validIndex = options.length > 0 
          ? Math.max(0, Math.min(correctIndex, options.length - 1))
          : 0;

        return {
          id: item[0] || '',
          question: item[1] || '',
          options: options,
          correctIndex: validIndex
        };
      }
      // Handle object format (backward compatibility)
      return {
        id: item.id || '',
        question: item.question || '',
        options: Array.isArray(item.options) ? item.options : [],
        correctIndex: typeof item.correctIndex === 'number' ? item.correctIndex : 0
      };
    });
  }

  // 3. Parse Tips (data.tip - tuple format: [id, questionId, suspectId, type, voice, text, difficulty])
  let tips: Tip[] = [];
  const rawTips = getValue(['tip', 'Tip', 'tips', 'Tips']) || [];

  if (Array.isArray(rawTips)) {
    tips = rawTips.map((item: any) => {
      if (Array.isArray(item)) {
        return {
          id: item[0] || '',
          questionId: item[1] || '',
          suspectId: item[2] || '',
          type: item[3] || 'detail',
          voice: item[4] || 'en-US-f1',
          text: item[5] || '',
          difficulty: typeof item[6] === 'number' ? item[6] : 0.8
        };
      }
      return {
        id: item.id || '',
        questionId: item.questionId || '',
        suspectId: item.suspectId || '',
        type: item.type || 'detail',
        voice: item.voice || 'en-US-f1',
        text: item.text || '',
        difficulty: item.difficulty || 0.8
      };
    });
  }

  // 4. Parse Timeline (data.tl - tuple format: [time, event])
  let timeline: Timeline[] = [];
  const rawTimeline = getValue(['tl', 'Tl', 'TL', 'timeline', 'Timeline']) || [];

  if (Array.isArray(rawTimeline)) {
    timeline = rawTimeline.map((item: any) => {
      if (Array.isArray(item)) {
        return {
          time: item[0] || '',
          event: item[1] || ''
        };
      }
      return {
        time: item.time || '',
        event: item.event || ''
      };
    });
  }

  // 5. Parse Evidence (data.ev - tuple format: [id, type, text])
  let evidence: Evidence[] = [];
  const rawEvidence = getValue(['ev', 'Ev', 'EV', 'evidence', 'Evidence']) || [];

  if (Array.isArray(rawEvidence)) {
    evidence = rawEvidence.map((item: any) => {
      if (Array.isArray(item)) {
        return {
          id: item[0] || '',
          type: item[1] || 'obj',
          text: item[2] || ''
        };
      }
      return {
        id: item.id || '',
        type: item.type || 'obj',
        text: item.text || ''
      };
    });
  }

  // 6. Parse Solution (data.sol - object with cul, how, why, key)
  let solution: Solution = {
    culpritSuspectId: '',
    how: '',
    why: '',
    key: ''
  };
  const rawSolution = getValue(['sol', 'Sol', 'SOL', 'solution', 'Solution']);

  if (rawSolution) {
    if (typeof rawSolution === 'object' && !Array.isArray(rawSolution)) {
      solution = {
        culpritSuspectId: rawSolution.cul || rawSolution.culpritSuspectId || '',
        how: rawSolution.how || '',
        why: rawSolution.why || '',
        key: rawSolution.key || ''
      };
    }
  }

  // 7. Parse Scoring Config (data.sc)
  // We intentionally ignore rawScoring from the DB to enforce global rebalancing.
  // Crime Game now yields ~200 points max to align with Training sessions.
  let scoring: ScoringConfig = {
    b: 100,
    pt: 10,
    bq: 20,
    trI: 10,
    trT: 10,
    trW: 1
  };

  // 8. Get suspect IDs list (data.sus)
  const suspectIds = getValue(['sus', 'Sus', 'SUS', 'suspectIds']) || [];

  // 9. Get other fields
  const title = getValue(['ttl', 'Ttl', 'TTL', 'title', 'Title']) || `Crime #${index + 1}`;
  const intro = getValue(['intro', 'Intro', 'INTRO', 'history', 'History', 'story', 'Story']) || 'Review the suspects and clues to solve this mystery.';
  const reveal = getValue(['rev', 'Rev', 'REV', 'reveal', 'Reveal']) || '';
  const date = getValue(['d', 'D', 'date', 'Date']) || '';
  const type = (getValue(['t', 'T', 'type', 'Type']) || 'missing') as 'missing' | 'murder';
  const caseId = getValue(['id', 'Id', 'ID']) || `case-${index}`;

  // Convert tips to clues for backward compatibility (tips become clues in the log)
  const clues: Clue[] = tips.map(tip => ({
    id: tip.id,
    type: 'text',
    content: tip.text,
    suspectId: tip.suspectId,
    difficulty: Math.round(tip.difficulty * 3) as 1 | 2 | 3
  }));

  return {
    id: caseId,
    date,
    type,
    title,
    intro,
    suspects,
    questions,
    tips,
    timeline,
    evidence,
    solution,
    reveal,
    scoring,
    suspectIds: Array.isArray(suspectIds) ? suspectIds : [],
    // Keep clues for backward compatibility
    clues
  };
}

// Fetch crime cases for a specific date
export async function fetchCrimeCasesByDate(dateKey: string): Promise<CrimeCase[]> {
  try {
    const casesRef = ref(db, `crimes/${dateKey}`);
    const snapshot = await get(casesRef);

    if (snapshot.exists()) {
      const data = snapshot.val();
      let cases: CrimeCase[] = [];

      // Detection logic for schema type

      // Helper to check if a node LOOKS like a case (has 'ch' or 'history')
      const isCaseNode = (node: any) => {
        if (typeof node !== 'object' || node === null) return false;
        // Check for suspects (ch) or history
        const keys = Object.keys(node);
        // Case-insensitive check
        const hasSuspects = keys.some(k => ['ch', 'suspects'].includes(k.toLowerCase()));
        const hasHistory = keys.some(k => ['history', 'intro', 'story'].includes(k.toLowerCase()));
        return hasSuspects || hasHistory;
      };

      // Type A: The node ITSELF is a case
      if (isCaseNode(data)) {
        cases = [normalizeRawCase(data)];
      }
      // Type B: Standard normalized schema { cases: [...] }
      else if (data.cases) {
         if (Array.isArray(data.cases)) {
           cases = data.cases.map((c: any, i: number) => normalizeRawCase(c, i));
         } else if (typeof data.cases === 'object') {
           cases = Object.values(data.cases).map((c: any, i: number) => normalizeRawCase(c, i));
         }
      }
      // Type C: Array of cases at root
      else if (Array.isArray(data)) {
        cases = data.map((c: any, i: number) => normalizeRawCase(c, i));
      }
      // Type D: Map of cases
      else {
         const potentialCases = Object.values(data);
         // Filter out primitives
         const validObjects = potentialCases.filter(v => typeof v === 'object' && v !== null);

         if (validObjects.length > 0) {
            cases = validObjects.map((c: any, i: number) => normalizeRawCase(c, i));
         }
      }

      return cases;
    }
    return [];
  } catch (error) {
    console.error(`Error fetching cases for ${dateKey}:`, error);
    return [];
  }
}

interface SubmitAttemptParams {
  dateKey: string;
  uid: string;
  displayName: string;
  photoURL?: string;
  result: 'win' | 'loss';
  guessedSuspectId: string;
  timeSpentSec: number;
  hintsUsed: number;
  deltaPoints: number;
}

// Submit attempt and update leaderboards
export async function submitAttemptAndUpdateLeaderboard(params: SubmitAttemptParams) {
  const { dateKey, uid, displayName, photoURL, result, guessedSuspectId, timeSpentSec, hintsUsed, deltaPoints } = params;

  const updates: any = {};
  const timestamp = serverTimestamp();

  // 1. Save attempt
  const attemptsRef = ref(db, `crimeGameAttempts/${dateKey}/${uid}`);
  const newAttemptRef = push(attemptsRef);
  const attemptData: Omit<GameAttempt, 'id' | 'createdAt'> & { createdAt: object } = {
    result,
    guessedSuspectId,
    timeSpentSec,
    hintsUsed,
    createdAt: timestamp
  };
  updates[`crimeGameAttempts/${dateKey}/${uid}/${newAttemptRef.key}`] = attemptData;

  // 2. Transaction for Global Leaderboard
  const globalRef = ref(db, `crimeGameLeaderboard/global/users/${uid}`);

  // 3. Transaction for Daily Leaderboard
  const dailyRef = ref(db, `crimeGameLeaderboard/daily/${dateKey}/users/${uid}`);

  // Helper to update stats
  const updateStats = (currentData: GameLeaderboardEntry | null) => {
    const stats: GameLeaderboardEntry = currentData || {
      uid,
      displayName,
      photoURL,
      points: 0,
      wins: 0,
      losses: 0,
      streak: 0,
      updatedAt: Date.now()
    } as GameLeaderboardEntry;

    // Update profile info in case it changed
    stats.displayName = displayName;
    stats.photoURL = photoURL;
    stats.updatedAt = Date.now() as any;

    stats.points = (stats.points || 0) + deltaPoints;
    if (stats.points < 0) stats.points = 0; // Floor at 0

    if (result === 'win') {
      stats.wins = (stats.wins || 0) + 1;
      stats.streak = (stats.streak || 0) + 1;
    } else {
      stats.losses = (stats.losses || 0) + 1;
      stats.streak = 0;
    }

    // Daily Participation Streak Logic
    // We use the current date (when the attempt is made) to calculate streak
    const todayStr = getTodayString();
    const lastPlayed = stats.lastPlayedDate;

    if (!lastPlayed) {
      // First time playing
      stats.dailyStreak = 1;
      stats.lastPlayedDate = todayStr;
    } else if (isSameDay(lastPlayed, todayStr)) {
      // Already played today, streak doesn't change
      // Ensure dailyStreak is at least 1
      stats.dailyStreak = stats.dailyStreak || 1;
    } else if (isConsecutiveDay(lastPlayed, todayStr)) {
      // Played yesterday, increment streak
      stats.dailyStreak = (stats.dailyStreak || 0) + 1;
      stats.lastPlayedDate = todayStr;
    } else if (isAfterConsecutive(lastPlayed, todayStr)) {
      // Missed a day (or more), reset streak
      stats.dailyStreak = 1;
      stats.lastPlayedDate = todayStr;
    }
    // If playing in the past (time travel?), don't update streak

    return stats;
  };

  try {
    // Execute transactions safely
    await Promise.all([
      runTransaction(globalRef, (current) => updateStats(current)),
      runTransaction(dailyRef, (current) => updateStats(current))
    ]);

    // Push the attempt
    await update(ref(db), updates);

    // Update lastPractice on the user node
    await update(ref(db, `users/${uid}`), { lastPractice: Date.now() });

    // Update challenge score if applicable
    if (deltaPoints > 0) {
      await updateUserScores(uid, deltaPoints, { displayName, profilePhotoUrl: photoURL } as any);
      await logUserActivity(uid, displayName || "User", "crime_game", deltaPoints, "Crime Game Solved");
      await incrementChallengeScore(uid, deltaPoints, 'crime_game');
    }

  } catch (error) {
    console.error("Error updating leaderboard/attempt:", error);
    throw error;
  }
}

export async function fetchLeaderboard(type: 'global' | 'daily', dateKey?: string): Promise<GameLeaderboardEntry[]> {
  let path = '';
  if (type === 'global') {
    path = 'crimeGameLeaderboard/global/users';
  } else if (type === 'daily' && dateKey) {
    path = `crimeGameLeaderboard/daily/${dateKey}/users`;
  } else {
    return [];
  }

  const lbRef = ref(db, path);

  try {
    // Realtime DB sorts ascending. We want top points (descending).
    // So we fetch last 10 by 'points'.
    const q = query(lbRef, orderByChild('points'), limitToLast(10));

    const snapshot = await get(q);
    if (snapshot.exists()) {
      const data = snapshot.val();
      const entries: GameLeaderboardEntry[] = Object.values(data);
      // Sort descending by points
      return entries.sort((a, b) => b.points - a.points);
    }
    return [];
  } catch (error: any) {
    // Robust fallback: if index is missing, fetch all and sort client-side
    if (error.message && error.message.includes("Index not defined")) {
      console.warn("Leaderboard index missing, falling back to client-side sort.");
      try {
        const snapshot = await get(lbRef);
        if (snapshot.exists()) {
          const data = snapshot.val();
          const entries: GameLeaderboardEntry[] = Object.values(data);
          return entries
            .sort((a, b) => b.points - a.points)
            .slice(0, 10);
        }
      } catch (innerError) {
        console.error("Error fetching leaderboard (fallback):", innerError);
      }
    } else {
      console.error("Error fetching leaderboard:", error);
    }
    return [];
  }
}

export async function fetchCurrentUserLeaderboardEntry(
  uid: string,
  type: 'global' | 'daily',
  dateKey?: string
): Promise<GameLeaderboardEntry | null> {
  let path = '';
  if (type === 'global') {
    path = `crimeGameLeaderboard/global/users/${uid}`;
  } else if (type === 'daily' && dateKey) {
    path = `crimeGameLeaderboard/daily/${dateKey}/users/${uid}`;
  } else {
    return null;
  }

  const userRef = ref(db, path);

  try {
    const snapshot = await get(userRef);
    if (snapshot.exists()) {
      const entry = snapshot.val() as GameLeaderboardEntry;
      
      // Validate and correct streak if needed
      // Only validate for global leaderboard (daily leaderboard is per-date)
      if (type === 'global' && entry.lastPlayedDate) {
        const todayStr = getTodayString();
        const lastPlayed = entry.lastPlayedDate;
        
        // If lastPlayedDate is more than 1 day ago, reset streak to 0
        if (isAfterConsecutive(lastPlayed, todayStr)) {
          // Check if streak needs to be reset
          if (entry.dailyStreak && entry.dailyStreak > 0) {
            // Update Firebase to persist the correction
            try {
              await update(userRef, { dailyStreak: 0 });
              entry.dailyStreak = 0;
            } catch (updateError) {
              console.error("Error updating streak:", updateError);
              // Still return corrected value even if update fails
              entry.dailyStreak = 0;
            }
          }
        }
      }
      
      return entry;
    }
    return null;
  } catch (error) {
    console.error("Error fetching current user leaderboard entry:", error);
    return null;
  }
}

// Fetch user attempts for a specific date
export async function fetchUserAttempts(dateKey: string, uid: string): Promise<GameAttempt[]> {
  try {
    const attemptsRef = ref(db, `crimeGameAttempts/${dateKey}/${uid}`);
    const snapshot = await get(attemptsRef);

    if (snapshot.exists()) {
      const data = snapshot.val();
      return Object.entries(data).map(([key, value]: [string, any]) => ({
        id: key,
        ...value,
        createdAt: value.createdAt || 0
      }));
    }
    return [];
  } catch (error) {
    console.error("Error fetching user attempts:", error);
    return [];
  }
}

// Fetch activity for the last 7 days (including today)
// Returns array of booleans in chronological order (Day-6, Day-5, ..., Today)
export async function fetchCrimeWeekActivity(uid: string): Promise<boolean[]> {
  const dates: string[] = [];
  const today = new Date();

  // Generate last 7 days
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);

    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    dates.push(`${day}-${month}-${year}`);
  }

  try {
    const results = await Promise.all(
      dates.map(dateKey => fetchUserAttempts(dateKey, uid))
    );
    return results.map(attempts => attempts.length > 0);
  } catch (error) {
    console.error("Error fetching crime week activity:", error);
    // Return all false on error to avoid breaking UI
    return new Array(7).fill(false);
  }
}
